import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, FileTextIcon } from 'lucide-react';
import { SectionLabel } from '../components/SectionLabel';
import { SERVICE_AREAS, getServiceArea } from '../data/serviceAreas';
export function ServiceArea() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const area = getServiceArea(slug);
  if (!area) {
    return <Navigate to="/" replace data-id="element-211" data-mp="38u8gji7esrqr" />;
  }
  const Icon = area.icon;
  const otherAreas = SERVICE_AREAS.filter(item => item.slug !== area.slug);
  return <main data-id="element-212" data-mp="3003x3juluil9">
      <section className="w-full bg-vm-bg px-6 pb-20 pt-40 lg:px-10 lg:pb-24 lg:pt-44" data-id="element-213" data-mp="2zzu6pv5ne9f1">
        <div className="mx-auto max-w-7xl" data-id="element-214" data-mp="2v22bar5so563">
          <Link to="/#servicios" className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-vm-muted transition-colors hover:text-white" data-id="element-215" data-mp="2v3dh0wisvmzu">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-216" data-mp="2l5fs06t49m66" />
            Servicios
          </Link>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="mt-10" data-id="element-217" data-mp="17iylnezi73tx">
            <SectionLabel tone="cream" data-id="element-218" data-mp="12lgk49ntfqvy">{area.name}</SectionLabel>
            <h1 className="mt-6 max-w-4xl font-bornia text-4xl font-bold leading-[1.03] text-white sm:text-5xl lg:text-[56px]" data-id="element-219" data-mp="12jlz6vcjnyrv">
              {area.tagline}
            </h1>
            <p className="mt-7 max-w-2xl text-lg font-light leading-relaxed text-vm-muted" data-id="element-220" data-mp="1v92q1okin7lz">{area.intro}</p>

            <div className="mt-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-vm-sky text-vm-bg" data-id="element-221" data-mp="1gclbypkxzevq">
              <Icon className="h-7 w-7" strokeWidth={1.5} aria-hidden="true" data-id="element-222" data-mp="1lad7dtkspj4o" />
            </div>
          </motion.div>
        </div>
      </section>

      <section aria-label={`Servicios de ${area.name}`} className="w-full bg-vm-cream py-24 lg:py-32" data-id="element-223" data-mp="9xkhkobkf4r9">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-224" data-mp="2cjzjgxn91l2h">
          <SectionLabel tone="blue" data-id="element-225" data-mp="2clj0iqgqc05b">Qué incluye</SectionLabel>
          <h2 className="mt-6 max-w-3xl font-bornia text-3xl font-bold leading-[1.05] text-vm-bg sm:text-4xl lg:text-[44px]" data-id="element-226" data-mp="27lxz89e37fpf">
            Servicios específicos de {area.name}
          </h2>

          <div className="mt-14 grid gap-4 sm:grid-cols-2" data-id="element-227" data-mp="1xtscpf6ltqhm">
            {area.items.map((item, index) => <motion.article key={item.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: index % 2 * 0.08
          }} className="rounded-2xl border border-vm-bg bg-white p-8 lg:p-10" data-id="element-228" data-mp="1swibz3drlseh" data-mpi={index}>
                <h3 className="font-bornia text-2xl font-semibold text-vm-bg lg:text-[26px]" data-id="element-229" data-mp="3e7qu2exrdm2z">{item.title}</h3>
                <p className="mt-3 text-base font-light leading-relaxed text-vm-navy" data-id="element-230" data-mp="39aiac9wyp9zm">{item.desc}</p>
              </motion.article>)}
          </div>

          <Link to="/#contacto" className="mt-14 inline-flex items-center gap-3 rounded-2xl bg-vm-bg px-7 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-vm-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-vm-electric focus-visible:ring-offset-4 focus-visible:ring-offset-vm-cream" data-id="element-231" data-mp="3ls2rm8c7d949">
            <FileTextIcon className="h-5 w-5" aria-hidden="true" data-id="element-232" data-mp="32sxajialj3oy" />
            Conversemos tu proyecto
          </Link>
        </div>
      </section>

      <section aria-label="Otras áreas de servicio" className="w-full bg-vm-bg py-24 lg:py-32" data-id="element-233" data-mp="37uwf2vi2o6nh">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-234" data-mp="3cpti9lso7lsg">
          <SectionLabel tone="cream" data-id="element-235" data-mp="n1j5jg06tseh">Otras áreas</SectionLabel>
          <div className="mt-10 grid gap-3 sm:grid-cols-3" data-id="element-236" data-mp="s1nln4x6ol46">
            {otherAreas.map(other => {
            const OtherIcon = other.icon;
            return <Link key={other.slug} to={`/servicios/${other.slug}`} className="group flex items-start justify-between gap-4 rounded-2xl border border-white/20 p-6 transition-colors duration-300 hover:border-vm-cream hover:bg-vm-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-vm-cream focus-visible:ring-offset-2 focus-visible:ring-offset-vm-bg" data-id="element-237" data-mp="pp3b2tpn5o1f">
                  <span data-id="element-238" data-mp="14kt1d41v0nby">
                    <OtherIcon className="h-7 w-7 text-vm-sky transition-colors duration-300 group-hover:text-vm-bg" strokeWidth={1.5} aria-hidden="true" data-id="element-239" data-mp="14j895l30pgh9" />
                    <span className="mt-4 block font-bornia text-xl font-semibold text-white transition-colors duration-300 group-hover:text-vm-bg" data-id="element-240" data-mp="1dexmr7y856k3">
                      {other.name}
                    </span>
                  </span>
                  <ArrowRightIcon className="h-5 w-5 shrink-0 text-vm-muted transition-all duration-300 group-hover:translate-x-1 group-hover:text-vm-bg" aria-hidden="true" data-id="element-241" data-mp="26w19jmj2pfay" />
                </Link>;
          })}
          </div>
        </div>
      </section>
    </main>;
}